import React, { useState } from "react";

const COMPARISON = [
  { symbol: ">",  label: "Greater than",             desc: "Value is strictly larger than the threshold" },
  { symbol: "<",  label: "Less than",                desc: "Value is strictly smaller than the threshold" },
  { symbol: "≥",  label: "Greater than or equal to", desc: "Value equals or exceeds the threshold" },
  { symbol: "≤",  label: "Less than or equal to",    desc: "Value equals or is below the threshold" },
  { symbol: "=",  label: "Equal to",                 desc: "Value exactly matches the stated quantity" },
  { symbol: "≠",  label: "Not equal to",             desc: "Value differs from the stated quantity" },
  { symbol: "≈",  label: "Approximately equal",      desc: "Value is close to, but not necessarily exact" },
];

const LOGICAL = [
  { symbol: "∧",  label: "AND (logical conjunction)", desc: "Both conditions must be true simultaneously" },
  { symbol: "∨",  label: "OR (logical disjunction)",  desc: "At least one of the conditions must be true" },
  { symbol: "¬",  label: "NOT (negation)",            desc: "The condition must be false / inverted" },
  { symbol: "∈",  label: "Element of",                desc: "Belongs to the specified set or range" },
  { symbol: "∉",  label: "Not an element of",         desc: "Does not belong to the specified set" },
  { symbol: "∪",  label: "Union",                     desc: "Combined set of all values from both groups" },
  { symbol: "∩",  label: "Intersection",              desc: "Values common to both groups" },
];

const ARITHMETIC = [
  { symbol: "+",  label: "Plus / Addition",           desc: "Sum of two or more values" },
  { symbol: "-",  label: "Minus / Subtraction",       desc: "Difference between two values" },
  { symbol: "×",  label: "Multiply",                  desc: "Product of two values" },
  { symbol: "÷",  label: "Divide",                    desc: "Quotient of two values" },
];
const CALCULUS = [
  { symbol: "d/dt", label: "Derivative (rate of change)", desc: "How fast a quantity is changing over time" },
  { symbol: "∫",    label: "Integral",                    desc: "Accumulated total of a quantity over an interval" },
  { symbol: "∂",    label: "Partial derivative",          desc: "Rate of change w.r.t. one variable while others are held constant" },
  { symbol: "lim",  label: "Limit",                       desc: "Value a function approaches as input nears a point" },
];

const STATISTICS = [
  { symbol: "μ",   label: "Mu / Mean / Average",  desc: "Arithmetic average of a dataset" },
  { symbol: "σ",   label: "Sigma / Std. Deviation",desc: "How spread out the values are around the mean" },
  { symbol: "σ²",  label: "Variance",              desc: "Average of the squared deviations from the mean" },
  { symbol: "P(A)", label: "Probability",          desc: "Likelihood of event A occurring (0 = impossible, 1 = certain)" },
  { symbol: "x̄",   label: "Sample mean",           desc: "Average value computed from a sample" },
  { symbol: "R²",  label: "R-squared",             desc: "Coefficient of determination; model fit quality (0–1)" },
];

const ARITHMETIC_EXTRA = [
  { symbol: "x²",  label: "Squared",          desc: "A value raised to the power of 2" },
  { symbol: "√x",  label: "Square root",      desc: "The non-negative root of a value" },
  { symbol: "Σ",   label: "Sigma / Summation",desc: "Sum of a series of values" },
  { symbol: "Δ",   label: "Delta / Change",   desc: "Difference or change in a quantity (Δx = x₂ – x₁)" },
  { symbol: "|x|", label: "Absolute value",   desc: "Magnitude of x, ignoring sign (always ≥ 0)" },
  { symbol: "%",   label: "Percent",          desc: "Parts per hundred, relative proportion" },
];
const Section = ({ title, items, open, onToggle }) => (
  <div style={{ borderBottom: "0.5px solid #e2e4e8" }}>
    <div
      onClick={onToggle}
      style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "9px 14px", cursor: "pointer", background: open ? "#f5f6f8" : "transparent" }}
    >
      <span style={{ fontSize: 11, fontWeight: 600, color: "#5a6070",
        letterSpacing: 0.5, textTransform: "uppercase" }}>{title}</span>
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
        stroke="#8a909e" strokeWidth="2"
        style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform .2s" }}>
        <polyline points="6 9 12 15 18 9"/>
      </svg>
    </div>
    {open && (
      <div style={{ padding: "4px 14px 10px" }}>
        {items.map((item, i) => (
          <div key={i} style={{ display: "flex", gap: 10, padding: "5px 0",
            borderBottom: i < items.length - 1 ? "0.5px solid #f0f1f3" : "none" }}>
            <div style={{
              width: 24, height: 24, borderRadius: 5, background: "#f5f6f8",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 13, fontWeight: 600, color: "#1a9e78", flexShrink: 0,
              fontFamily: "monospace",
            }}>
              {item.symbol}
            </div>
            <div>
              <div style={{ fontSize: 11.5, fontWeight: 500, color: "#1a1e2e" }}>{item.label}</div>
              <div style={{ fontSize: 10.5, color: "#8a909e", marginTop: 1, lineHeight: 1.4 }}>{item.desc}</div>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

const SymbolAbbreviations = () => {
  const [open, setOpen] = useState({ comparison: true, logical: true, arithmetic: false,  calculus: false,
  statistics: false,});
  const toggle = (k) => setOpen(p => ({ ...p, [k]: !p[k] }));

  return (
    <div style={{ background: "#fff", border: "0.5px solid #e2e4e8",
      borderRadius: 10, overflow: "hidden" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 7,
        padding: "10px 14px", borderBottom: "0.5px solid #e2e4e8", background: "#f5f6f8" }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#1a9e78" strokeWidth="2">
          <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
          <rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/>
        </svg>
        <span style={{ fontSize: 13, fontWeight: 500, color: "#1a1e2e" }}>Symbol Abbreviations</span>
      </div>
      <p style={{ fontSize: 11, color: "#8a909e", padding: "8px 14px",
        lineHeight: 1.5, margin: 0, borderBottom: "0.5px solid #e2e4e8" }}>
        These symbols appear in the <em>Generation Criteria</em> section of each action card.
      </p>
      <Section title="Comparison Operators" items={COMPARISON} open={open.comparison} onToggle={() => toggle("comparison")} />
      <Section title="Logical / Set Operators" items={LOGICAL}    open={open.logical}    onToggle={() => toggle("logical")}    />
      <Section title="Arithmetic & Algebraic"  items={ARITHMETIC} open={open.arithmetic} onToggle={() => toggle("arithmetic")} />
   <Section title="Arithmetic & Algebraic (Extended)" items={ARITHMETIC_EXTRA} open={open.arithmetic_extra} onToggle={() => toggle("arithmetic_extra")} />
   <Section title="Calculus & Rates"       items={CALCULUS}    open={open.calculus}   onToggle={() => toggle("calculus")}   />
<Section title="Statistics & Probability" items={STATISTICS} open={open.statistics} onToggle={() => toggle("statistics")} /> </div>
  );
};

export default SymbolAbbreviations;